import { useMsal } from '@azure/msal-react';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, makeStyles, mergeClasses } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useCommonStyles, useStores } from '../../../common/hooks';
import { Nomination } from '../../../models';
import { ValidationMessageList } from '../../shared-components';
import { CountrySelectionDropdown } from '../../shared-components/country-selection-dropdown/CountrySelectionDropdown';

const useStyles = makeStyles({
  surface: { maxWidth: '415px' },
  body: { gap: '24px' },
});

type AssignCountriesAssociationModalLoadingType = 'save' | undefined;

interface AssignCountriesAssociationModalProps {
  isModalOpen: boolean;
  selectedNomination?: Nomination;
  onDismissModal(needRefresh?: boolean): void;
}

const AssignCountriesAssociationModalImpl: React.FC<AssignCountriesAssociationModalProps> = (props) => {
  const {
    isModalOpen,
    selectedNomination,
    onDismissModal,
  } = props;

  const {
    instance,
  } = useMsal();

  const {
    apiStore,
    nominationForStaffPageUiStore,
  } = useStores();

  const styles = useStyles();
  const commonStyles = useCommonStyles();

  const [loadingType, setLoadingType] = useState<AssignCountriesAssociationModalLoadingType>();
  const [isBeingValidated, setIsBeingValidated] = useState<boolean>(false);

  const updateNominationAssociatedCountries = async (nominationId: number): Promise<void> => {
    try {
      await apiStore.updateNominationCountries(instance, nominationId, nominationForStaffPageUiStore.editingCountryIds);
    } catch (error) {
      // TODO: Handle error
    }
  }

  const handleSave = async (): Promise<void> => {
    setIsBeingValidated(true);
    if (!nominationForStaffPageUiStore.nominationForStaff || !nominationForStaffPageUiStore.associatedCountriesRequestValidation.isValid) return;

    setLoadingType('save');

    await updateNominationAssociatedCountries(nominationForStaffPageUiStore.nominationForStaff.nominationId);

    setLoadingType(undefined);

    onDismissModal(true);
  }

  const preloadData = (): void => {
    if (selectedNomination && nominationForStaffPageUiStore.editAssociatedCountryModalType === 'update') {
      nominationForStaffPageUiStore.createEditAssociatedCountryRequestBodyForUpdate(selectedNomination);
    }
  }

  useEffect(() => {
    preloadData();
    return () => {
      setIsBeingValidated(false);
      nominationForStaffPageUiStore.resetAssignCountriesModalStates();
    }
  }, [isModalOpen]);

  return (
    <Dialog
      open={isModalOpen}
      onOpenChange={(_, data) => {
        if (!data.open) onDismissModal();
      }}
    >
      <DialogSurface className={mergeClasses('assign-country-association-modal', commonStyles.modalSurface, styles.surface)}>
        <DialogBody className={styles.body}>
          <DialogTitle
            action={
              <Button
                appearance='transparent'
                title='Close'
                icon={<DismissRegular />}
                onClick={() => onDismissModal()}
              />
            }
          >
            Assign country association
          </DialogTitle>
          <DialogContent>
            <CountrySelectionDropdown
              id='associated-countries'
              associatedCountries={nominationForStaffPageUiStore.editingAssociatedCountries}
              required
              validationMessage={isBeingValidated && nominationForStaffPageUiStore.associatedCountriesRequestValidation.fieldErrors.associatedCountries?.length
                ? <ValidationMessageList
                    messages={nominationForStaffPageUiStore.associatedCountriesRequestValidation.fieldErrors.associatedCountries} />
                : undefined}
            />
          </DialogContent>
          <DialogActions>
            <Button
              appearance='secondary'
              onClick={() => onDismissModal()}
            >
              Cancel
            </Button>
            <Button
              appearance='primary'
              disabled={loadingType === 'save' || (isBeingValidated && !nominationForStaffPageUiStore.associatedCountriesRequestValidation.isValid)}
              onClick={handleSave}
            >
              Save
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export const AssignCountriesAssociationModal = observer(AssignCountriesAssociationModalImpl);
